import {
  Selectors,
  useWidgetState,
} from '@eCommerce/frontend-legacy-shared-library-native';
import {useRoute} from '@react-navigation/native';
import {useCallback} from 'react';
import {Platform} from 'react-native';

import {trackEvent} from 'utils/trackEvent';

/**
 * This is a hook for tracking the Page View event.
 * @returns a function that can be called when the page/screen is in focus
 * @example
 * const {trackPageViewEvent} = usePageViewEvent();
 * useFocusEffect(trackPageViewEvent);
 *
 * e.g: https://caesars.com/rewards/profile
 * {
    "event": "pageview",
    "pageName": "My Account",
    "L1": "rewards",
    "L2": "profile",
    "L3": "",
    "signinStatus": "signedIn",
    "tr_number": "11503550945",
    "tier": "GLD",
    }
 *}
 *
 */

export const usePageViewEvent = () => {
  const {name: pageName} = useRoute();
  const isLoggedIn = useWidgetState(Selectors.isLoggedInSelector);
  const tier = useWidgetState(Selectors.userExtendedTierCodeSelector) || '';
  const tr_number = useWidgetState(Selectors.userAccountIdSelector) || '';

  const url = Platform.OS === 'web' ? new URL(window.location.href) : undefined;

  const pathNameArray = url?.pathname.startsWith('/')
    ? url?.pathname.replace('/', '').split('/')
    : url?.pathname.split('/');

  const trackPageViewEvent = useCallback(
    () =>
      trackEvent({
        event: 'pageview',
        pageName,
        L1: pathNameArray?.[0] || '',
        L2: pathNameArray?.[1] || '',
        L3: pathNameArray?.[2] || '',
        signinStatus: isLoggedIn ? 'signedIn' : 'notSignedIn',
        tr_number,
        tier: isLoggedIn ? tier : '', // the tier selector will return GOLD by default, even if user is undefined / not logged in
      }),
    [isLoggedIn, pageName, pathNameArray, tier, tr_number]
  );

  return {trackPageViewEvent};
};
